import { i, x } from './index-DgkZLaK4.js';

/**
 * @class TritonXUserSession
 * @extends LitElement
 * @description Custom Element for handling Triton x user session
 */
class TritonXUserSession extends i {
  static properties = {
    openModal: { state: true },
  };

  /** @type {String | null} */
  domain = this.getAttribute('domain') || null;

  /** @type {String | null} */
  hmac = this.getAttribute('hmac') || null;

  /** @type {String | null} */
  endpoint = this.getAttribute('endpoint') || null;

  /** @type {String | null} */
  path = this.getAttribute('path') || null;

  /** @type {String | null} */
  salt = this.getAttribute('salt') || null;

  /** @type {String | null} */
  userSessionId = this.getAttribute('user-session-id') || null;

  /** @type {String | null} */
  userId = this.getAttribute('user-id') || null;

  /** @type {String | null} */
  shopifyCustomerId = this.getAttribute('shopify-customer-id') || null;

  /** @type {Number | null} */
  sessionFetchInterval = null;

  /** @type {AbortController} */
  abortController = new AbortController();

  constructor() {
    super();

    this.openModal = false;

    // Check if all required attributes are set, if not remove the cookie
    if (
      !this.endpoint ||
      !this.path ||
      !this.domain ||
      !this.hmac ||
      !this.salt ||
      !this.userSessionId ||
      !this.userId ||
      !this.shopifyCustomerId
    ) {
      this.endUserSession();
      return;
    }

    // Listen for visibility change so we can stop the interval if the user is not on the page and check it directly again when the user comes back
    document.addEventListener(
      'visibilitychange',
      () => {
        document.visibilityState === 'hidden' ? this.stopInterval() : this.getUserSession();
      },
      {
        signal: this.abortController.signal,
      }
    );

    this.getUserSession();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopInterval();
    this.abortController.abort();
  }

  /**
   * @method startInterval
   * @description Start the interval to fetch the user session. We are extending the session every 1/2 minute for the user so the session stays active
   * @returns {void}
   */
  startInterval() {
    // Guard if there is already an interval running
    if (this.sessionFetchInterval) return;

    console.log(' passing the guard interval already running check');

    this.sessionFetchInterval = setInterval(async () => {
      await this.getUserSession();
    }, 30000);
  }

  stopInterval() {
    clearInterval(this.sessionFetchInterval);
    this.sessionFetchInterval = null;
  }

  async getUserSession() {
    // Check the user session or extend the user session by calling the endpoint again by default
    let { isSessionActive = false } = await this.fetchUserSession();

    // Set the user session cookie when isSessionActive is true
    isSessionActive === true ? this.startInterval() : this.endUserSession();
  }

  /**
   * @method fetchUserSession
   * @description Fetch the user session from the triton x api. If the user session is already active, it will extend the session by calling the endpoint again.
   * @returns {Object}
   */
  async fetchUserSession() {
    let options = {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        'X-Shopify-Shop-Domain': this.domain,
        'X-Shopify-HMAC-Sha256': this.hmac,
      },
    };
    const response = await fetch(
      `${this.endpoint}${this.path}?userSessionId=${this.userSessionId}&userId=${this.userId}&customerId=${this.shopifyCustomerId}&salt=${this.salt}`,
      options
    );

    if (!response.ok) {
      throw new Error('Error occurred! User session could not be fetched.');
    }

    const data = await response.json();
    return data;
  }

  /**
   * @method endUserSession
   * @description Delete the cookie and open the modal.
   */
  endUserSession() {
    this.stopInterval();
    this.openModal = true;
  }

  /**
   * @method updated
   * @description Lit lifecycle method. Used to set up focus trap when modal opens.
   */
  updated() {
    if (this.openModal === true) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => trapFocus(this));
    } else {
      removeTrapFocus(this);
    }
  }

  /**
   * @method render
   * @description Render the modal.
   * @returns {HTMLElement}
   */
  render() {
    return this.openModal
      ? x`
          <div class="fixed inset-0 z-modal-backdrop block h-full w-full bg-black/50"></div>
          <div class="fixed inset-0 z-modal h-full w-full overflow-auto px-6 py-12">
            <div
              class="modal-content mx-auto w-full max-w-[400px] space-y-4 rounded bg-white p-8"
              tabindex="-1"
            >
              <p class="block text-2xl font-bold">${triton_x.session_expired.title}</p>
              <p>${triton_x.session_expired.description}</p>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="button button--blue"
                  @click=${() => {
                    window.location.href = routes.account_logout_url;
                  }}
                >
                  ${triton_x.session_expired.button_label}
                </button>
              </div>
            </div>
          </div>
        `
      : null;
  }

  createRenderRoot() {
    return this;
  }
}

customElements.define('triton-x-user-session', TritonXUserSession);

export { TritonXUserSession as default };
